import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Download, Eye, Loader2, Video } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { getDeckName, getDeckDescription } from '../utils/deckLocalization';

// Display labels + visual variant per badge value.
const BADGE_META = {
  featured:   { label: 'Featured',           variant: 'default' },
  new:        { label: 'New',                variant: 'success' },
  updated:    { label: 'Updated',            variant: 'secondary' },
  tournament: { label: 'Tournament Winning', variant: 'warning' },
  'staff-pick': { label: 'Staff Pick',       variant: 'default' },
  budget:     { label: 'Budget Friendly',    variant: 'secondary' },
  beginner:   { label: 'Beginner Friendly',  variant: 'success' },
  'deck-profile': { label: 'Deck Profile',   variant: 'default' },
  'match-video':  { label: 'Match Video',    variant: 'warning' },
};

/**
 * DeckCard - Reusable card component for displaying deck previews
 * Used on Home page and Sample Decks page
 */
function DeckCard({
  deck,
  language = 'EN',
  onLoadDeck,
  showViewButton = true,
  showLoadButton = true,
  loading = false,
  className,
}) {
  const navigate = useNavigate();

  const name = getDeckName(deck, language);
  const description = getDeckDescription(deck, language);
  const badges = Array.isArray(deck.badges) ? deck.badges : [];
  const tags = Array.isArray(deck.tags) ? deck.tags : [];
  const videoCount = deck.videoCount || (Array.isArray(deck.videos) ? deck.videos.length : 0);

  const handleView = () => {
    navigate(`/decks/view/${deck.id}`);
  };

  const handleLoad = () => {
    if (onLoadDeck) {
      onLoadDeck(deck);
    }
  };

  return (
    <Card
      className={cn(
        'group overflow-hidden transition-all duration-300',
        'hover:shadow-xl hover:-translate-y-1',
        'border border-border/50',
        className
      )}
    >
      {/* Deck Image */}
      <div className="relative aspect-[3/4] overflow-hidden bg-muted">
        {deck.imageUrl ? (
          <img
            src={deck.imageUrl}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
            <span className="text-6xl">🃏</span>
          </div>
        )}

        {/* Top-left badge stack */}
        {badges.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
            {badges.slice(0, 3).map((b) => {
              const meta = BADGE_META[b] || { label: b, variant: 'outline' };
              return (
                <Badge key={b} variant={meta.variant} className="shadow-sm">
                  {meta.label}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Top-right video indicator */}
        {videoCount > 0 && (
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className="shadow-sm gap-1">
              <Video className="h-3 w-3" />
              {videoCount}
            </Badge>
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
          {showViewButton && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleView}
              className="gap-1"
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          )}
          {showLoadButton && onLoadDeck && (
            <Button
              variant="default"
              size="sm"
              onClick={handleLoad}
              disabled={loading}
              className="gap-1"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Load
            </Button>
          )}
        </div>
      </div>

      {/* Deck Info */}
      <CardContent className="p-4">
        <h3 className="font-semibold text-base mb-1 truncate">
          {name}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2">
          {description}
        </p>
        {tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {tags.slice(0, 4).map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

DeckCard.propTypes = {
  deck: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    name_ja: PropTypes.string,
    name_ko: PropTypes.string,
    name_zhHant: PropTypes.string,
    description: PropTypes.string,
    description_ja: PropTypes.string,
    description_ko: PropTypes.string,
    description_zhHant: PropTypes.string,
    imageUrl: PropTypes.string,
    deckCode: PropTypes.string,
    badges: PropTypes.arrayOf(PropTypes.string),
    tags: PropTypes.arrayOf(PropTypes.string),
    videos: PropTypes.array,
    videoCount: PropTypes.number,
  }).isRequired,
  language: PropTypes.string,
  onLoadDeck: PropTypes.func,
  showViewButton: PropTypes.bool,
  showLoadButton: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default DeckCard;
